import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../utils/api';
import type { Employee, CreateEmployeeRequest, UpdateEmployeeRequest } from '../types';

interface EmployeeFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface UseEmployeesReturn {
  employees: Employee[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  refetch: (filters?: EmployeeFilters) => Promise<void>;
}

/**
 * Custom hook for fetching employee list with pagination and filters
 * 
 * @param initialFilters - Initial filter parameters
 * @returns Employee list data, loading state, error state, pagination info, and refetch function
 */
export const useEmployees = (initialFilters?: EmployeeFilters): UseEmployeesReturn => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null>(null);

  // Memoize filters to prevent infinite loop
  const memoizedFilters = useMemo(() => initialFilters, [
    initialFilters?.page,
    initialFilters?.limit,
    initialFilters?.search,
    initialFilters?.status,
    initialFilters?.sortBy,
    initialFilters?.sortOrder,
  ]);

  const fetchEmployees = useCallback(async (filters?: EmployeeFilters) => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getAllEmployees(filters || memoizedFilters);
      setEmployees(data.employees);
      setPagination(data.pagination);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch employees';
      setError(errorMessage);
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  }, [memoizedFilters]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  return {
    employees,
    loading,
    error,
    pagination,
    refetch: fetchEmployees,
  };
};

interface UseEmployeeReturn {
  employee: Employee | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching a single employee by ID
 * 
 * @param id - Employee ID
 * @returns Employee data, loading state, error state, and refetch function
 */
export const useEmployee = (id: string): UseEmployeeReturn => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployee = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await api.getEmployeeById(id);
      setEmployee(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch employee';
      setError(errorMessage);
      console.error('Error fetching employee:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  return {
    employee,
    loading,
    error,
    refetch: fetchEmployee,
  };
};

interface UseCreateEmployeeReturn {
  createEmployee: (data: CreateEmployeeRequest) => Promise<Employee | null>;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for creating a new employee
 * 
 * @returns Create function, loading state, and error state
 */
export const useCreateEmployee = (): UseCreateEmployeeReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createEmployee = async (data: CreateEmployeeRequest): Promise<Employee | null> => {
    try {
      setLoading(true);
      setError(null);
      const employee = await api.createEmployee(data);
      return employee;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create employee';
      setError(errorMessage);
      console.error('Error creating employee:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createEmployee,
    loading,
    error,
  };
};

interface UseUpdateEmployeeReturn {
  updateEmployee: (id: string, data: UpdateEmployeeRequest) => Promise<Employee | null>;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for updating an employee
 * 
 * @returns Update function, loading state, and error state
 */
export const useUpdateEmployee = (): UseUpdateEmployeeReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateEmployee = async (id: string, data: UpdateEmployeeRequest): Promise<Employee | null> => {
    try {
      setLoading(true);
      setError(null);
      const employee = await api.updateEmployee(id, data);
      return employee;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update employee';
      setError(errorMessage);
      console.error('Error updating employee:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateEmployee,
    loading,
    error,
  };
};

interface UseDeleteEmployeeReturn {
  deleteEmployee: (id: string) => Promise<boolean>;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for deleting an employee
 * 
 * @returns Delete function, loading state, and error state
 */
export const useDeleteEmployee = (): UseDeleteEmployeeReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteEmployee = async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      await api.deleteEmployee(id);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete employee';
      setError(errorMessage);
      console.error('Error deleting employee:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteEmployee,
    loading,
    error,
  };
};
